// DAS-2107 / DAS-2141: "Your Fundamentals" modal for PortfolioAnalysisView
// Three sections:
//   1. Tickers ranked by fundamental_final_score (sortable, with tooltips)
//   2. Tickers with fundamental_trend_label_change_magnitude > 1, grouped by direction
//   3. Tickers with highest & lowest risk_adj_q_return

import React, { useMemo, useState, useCallback } from 'react';
import {
  Box, Typography, Paper, Chip, Tooltip,
  Table as MuiTable, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TableSortLabel,
} from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RemoveIcon from '@mui/icons-material/Remove';
import InfoTooltip from '../InfoTooltip';
import ClickableTickerChip from './ClickableTickerChip';
import {
  FUNDAMENTAL_FIELD_DESCRIPTIONS,
  getLabelColor,
} from '../../helpers/fundamentalFieldDescriptions';

const num = (v, d = 2) => {
  const n = parseFloat(v);
  if (v == null || isNaN(n)) return '—';
  return n.toFixed(d);
};

const signed = (v, d = 3) => {
  const n = parseFloat(v);
  if (v == null || isNaN(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(d)}`;
};

const SectionTitle = ({ children }) => (
  <Typography
    variant="subtitle2"
    sx={{ mt: 3, mb: 1.5, color: 'text.secondary', textTransform: 'uppercase', fontSize: '0.7rem', letterSpacing: 1 }}
  >
    {children}
  </Typography>
);

const directionIcon = (dir) => {
  if (!dir) return <RemoveIcon sx={{ fontSize: 16, color: 'text.disabled' }} />;
  const d = String(dir).toLowerCase();
  if (d === 'up') return <ArrowUpwardIcon sx={{ fontSize: 16, color: '#2e7d32' }} />;
  if (d === 'down') return <ArrowDownwardIcon sx={{ fontSize: 16, color: '#c62828' }} />;
  return <RemoveIcon sx={{ fontSize: 16, color: 'text.disabled' }} />;
};

const directionColor = (dir) => {
  if (!dir) return undefined;
  const d = String(dir).toLowerCase();
  if (d === 'up') return '#2e7d32';
  if (d === 'down') return '#c62828';
  return undefined;
};

const buildTooltipText = (fieldKey) => {
  const desc = FUNDAMENTAL_FIELD_DESCRIPTIONS[fieldKey];
  if (!desc) return '';
  const parts = [desc.description];
  if (desc.calculation) parts.push(`Calculation: ${desc.calculation}`);
  if (desc.scale) parts.push(desc.scale);
  return parts.join('\n');
};

const SECTION1_COLUMNS = [
  { id: 'rank', label: '#', field: null, align: 'left', sortable: false },
  { id: 'ticker', label: 'Ticker', field: 'ticker', align: 'left', sortable: true },
  { id: 'fundamentalFinalScore', label: 'Final Score', field: 'fundamentalFinalScore', align: 'right', sortable: true, descKey: 'fundamental_final_score', bold: true },
  { id: 'fundamentalTrendScore', label: 'Trend Score', field: 'fundamentalTrendScore', align: 'right', sortable: true, descKey: 'fundamental_trend_score' },
  { id: 'fundamentalForwardScore', label: 'Forward Score', field: 'fundamentalForwardScore', align: 'right', sortable: true, descKey: 'fundamental_forward_score' },
  { id: 'fundamentalCredibility', label: 'Credibility', field: 'fundamentalCredibility', align: 'right', sortable: true, descKey: 'fundamental_credibility' },
  { id: 'fundamentalTrendLabel', label: 'Label', field: 'fundamentalTrendLabel', align: 'left', sortable: true, descKey: 'fundamental_trend_label' },
  { id: 'pe', label: 'P/E', field: 'pe', align: 'right', sortable: true, descKey: 'PE' },
  { id: 'priceToBook', label: 'P/B', field: 'priceToBook', align: 'right', sortable: true, descKey: 'priceToBook' },
  { id: 'roe', label: 'ROE', field: 'roe', align: 'right', sortable: true, descKey: 'roe' },
];

const SECTION3_COLUMNS = [
  { id: 'ticker', label: 'Ticker', field: 'ticker', align: 'left', sortable: true },
  { id: 'riskAdjQReturn', label: 'Risk-Adj Q', field: 'riskAdjQReturn', align: 'right', sortable: true, descKey: 'risk_adj_q_return', bold: true },
  { id: 'expectedQReturn', label: 'Expected Q', field: 'expectedQReturn', align: 'right', sortable: true, descKey: 'expected_q_return' },
  { id: 'returnProbability', label: 'Return Prob.', field: 'returnProbability', align: 'right', sortable: true, descKey: 'return_probability' },
  { id: 'residualVolatility', label: 'Resid. Vol.', field: 'residualVolatility', align: 'right', sortable: true, descKey: 'residual_volatility' },
];

const LABEL_RANK = {
  'strong improvement': 7,
  'moderate improvement': 6,
  'mild improvement': 5,
  'stable': 4,
  'mild deterioration': 3,
  'moderate deterioration': 2,
  'strong deterioration': 1,
};

const comparator = (a, b, field) => {
  const va = a[field];
  const vb = b[field];

  if (field === 'fundamentalTrendLabel') {
    const ra = LABEL_RANK[String(va || '').toLowerCase()] ?? 0;
    const rb = LABEL_RANK[String(vb || '').toLowerCase()] ?? 0;
    return ra - rb;
  }
  if (field === 'ticker' || field === 'company') {
    return String(va || '').localeCompare(String(vb || ''));
  }
  const na = parseFloat(va);
  const nb = parseFloat(vb);
  const aValid = !isNaN(na);
  const bValid = !isNaN(nb);
  if (!aValid && !bValid) return 0;
  if (!aValid) return -1;
  if (!bValid) return 1;
  return na - nb;
};

const PAModal_Fundamentals = ({ tpData, yfData, data, source = 'portfolio' }) => {
  const [s1OrderBy, setS1OrderBy] = useState('fundamentalFinalScore');
  const [s1Order, setS1Order] = useState('desc');
  const [s3TopOrderBy, setS3TopOrderBy] = useState('riskAdjQReturn');
  const [s3TopOrder, setS3TopOrder] = useState('desc');
  const [s3BotOrderBy, setS3BotOrderBy] = useState('riskAdjQReturn');
  const [s3BotOrder, setS3BotOrder] = useState('asc');

  const merged = useMemo(() => {
    const all = [];
    const tickerSet = new Set([...Object.keys(tpData), ...Object.keys(yfData)]);

    tickerSet.forEach(ticker => {
      const tp = tpData[ticker] || {};
      const yf = yfData[ticker] || {};
      const position = data.find(d => (d.Ticker || '').toUpperCase() === ticker.toUpperCase());

      all.push({
        ticker,
        company: position?.Company || '',
        fundamentalFinalScore: parseFloat(tp.fundamental_final_score),
        fundamentalTrendScore: parseFloat(tp.fundamental_trend_score),
        fundamentalTrendLabel: tp.fundamental_trend_label || '',
        fundamentalTrendLabelPrev: tp.fundamental_trend_label_previous || '',
        fundamentalTrendDirection: tp.fundamental_trend_label_direction || '',
        fundamentalChangeMagnitude: parseFloat(tp.fundamental_trend_label_change_magnitude),
        fundamentalForwardScore: parseFloat(tp.fundamental_forward_score),
        fundamentalCredibility: parseFloat(tp.fundamental_credibility),
        fundamentalText: tp.fundamental_trend_analysis_text || '',
        riskAdjQReturn: parseFloat(tp.risk_adj_q_return),
        expectedQReturn: parseFloat(tp.expected_q_return),
        returnProbability: parseFloat(tp.return_probability),
        residualVolatility: parseFloat(tp.residual_volatility),
        pe: parseFloat(yf.PE),
        priceToBook: parseFloat(yf.priceToBook),
        roe: parseFloat(yf.roe),
      });
    });

    return all;
  }, [tpData, yfData, data]);

  const handleS1Sort = useCallback((field) => {
    setS1Order(prev => (s1OrderBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setS1OrderBy(field);
  }, [s1OrderBy]);

  const handleS3TopSort = useCallback((field) => {
    setS3TopOrder(prev => (s3TopOrderBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'desc'));
    setS3TopOrderBy(field);
  }, [s3TopOrderBy]);

  const handleS3BotSort = useCallback((field) => {
    setS3BotOrder(prev => (s3BotOrderBy === field ? (prev === 'asc' ? 'desc' : 'asc') : 'asc'));
    setS3BotOrderBy(field);
  }, [s3BotOrderBy]);

  const byFinalScore = useMemo(() => {
    const valid = merged.filter(m => !isNaN(m.fundamentalFinalScore));
    return [...valid].sort((a, b) => {
      const c = comparator(a, b, s1OrderBy);
      return s1Order === 'asc' ? c : -c;
    });
  }, [merged, s1OrderBy, s1Order]);

  const labelChanges = useMemo(() => {
    const qualified = merged
      .filter(m => !isNaN(m.fundamentalChangeMagnitude) && m.fundamentalChangeMagnitude > 1);

    const dirOrder = { up: 0, down: 1 };
    return qualified.sort((a, b) => {
      const da = dirOrder[String(a.fundamentalTrendDirection).toLowerCase()] ?? 2;
      const db = dirOrder[String(b.fundamentalTrendDirection).toLowerCase()] ?? 2;
      if (da !== db) return da - db;
      return b.fundamentalChangeMagnitude - a.fundamentalChangeMagnitude;
    });
  }, [merged]);

  const byRiskAdjReturn = useMemo(() => {
    const valid = merged.filter(m => !isNaN(m.riskAdjQReturn));
    const sorted = [...valid].sort((a, b) => b.riskAdjQReturn - a.riskAdjQReturn);
    const top5 = sorted.slice(0, 5);
    const bottom5 = sorted.slice(-5).reverse();

    const sortedTop = [...top5].sort((a, b) => {
      const c = comparator(a, b, s3TopOrderBy);
      return s3TopOrder === 'asc' ? c : -c;
    });
    const sortedBot = [...bottom5].sort((a, b) => {
      const c = comparator(a, b, s3BotOrderBy);
      return s3BotOrder === 'asc' ? c : -c;
    });

    return { top5: sortedTop, bottom5: sortedBot };
  }, [merged, s3TopOrderBy, s3TopOrder, s3BotOrderBy, s3BotOrder]);

  if (merged.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" sx={{ minHeight: 200 }}>
        <Typography color="text.secondary">No fundamental data available</Typography>
      </Box>
    );
  }

  const renderHeaderCell = (col, orderBy, order, onSort) => {
    const tooltipText = col.descKey ? buildTooltipText(col.descKey) : '';
    return (
      <TableCell key={col.id} align={col.align} sx={{ fontWeight: 600 }}>
        <Box display="flex" alignItems="center" gap={0} sx={{ whiteSpace: 'nowrap' }}>
          {col.sortable && col.field ? (
            <TableSortLabel
              active={orderBy === col.field}
              direction={orderBy === col.field ? order : 'desc'}
              onClick={() => onSort(col.field)}
            >
              {col.label}
            </TableSortLabel>
          ) : (
            <span>{col.label}</span>
          )}
          {tooltipText && (
            <InfoTooltip text={tooltipText} size="small" />
          )}
        </Box>
      </TableCell>
    );
  };

  const renderLabelChip = (label) => {
    if (!label) return null;
    const colors = getLabelColor(label);
    return (
      <Chip
        label={label}
        size="small"
        sx={{
          height: 22,
          fontSize: '0.65rem',
          fontWeight: 600,
          bgcolor: colors.bg,
          color: colors.text,
          border: `1px solid ${colors.border}`,
        }}
      />
    );
  };

  return (
    <Box>
      {/* --- Section 1: Fundamental Score Ranking --- */}
      <SectionTitle>Fundamental Score Ranking</SectionTitle>
      <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: '40vh', mb: 2 }}>
        <MuiTable size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {SECTION1_COLUMNS.map(col => renderHeaderCell(col, s1OrderBy, s1Order, handleS1Sort))}
            </TableRow>
          </TableHead>
          <TableBody>
            {byFinalScore.map((row, idx) => (
              <TableRow key={row.ticker} hover>
                <TableCell sx={{ color: 'text.secondary' }}>{idx + 1}</TableCell>
                <TableCell>
                  <ClickableTickerChip ticker={row.ticker} source={source} />
                  {row.company && (
                    <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                      {row.company}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>{num(row.fundamentalFinalScore)}</TableCell>
                <TableCell align="right">{num(row.fundamentalTrendScore)}</TableCell>
                <TableCell align="right">{num(row.fundamentalForwardScore)}</TableCell>
                <TableCell align="right">{num(row.fundamentalCredibility)}</TableCell>
                <TableCell>{renderLabelChip(row.fundamentalTrendLabel)}</TableCell>
                <TableCell align="right">{num(row.pe, 1)}</TableCell>
                <TableCell align="right">{num(row.priceToBook, 1)}</TableCell>
                <TableCell align="right">{num(row.roe)}</TableCell>
              </TableRow>
            ))}
            {byFinalScore.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">
                  <Typography variant="caption" color="text.secondary">No scores available</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </MuiTable>
      </TableContainer>

      {/* --- Section 2: Significant Label Changes (magnitude > 1) --- */}
      <SectionTitle>Significant Fundamental Label Changes (magnitude &gt; 1)</SectionTitle>
      {labelChanges.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          No tickers with label change magnitude greater than 1
        </Typography>
      ) : (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          {labelChanges.map((row) => {
            const color = directionColor(row.fundamentalTrendDirection);
            return (
              <Box
                key={row.ticker}
                display="flex"
                alignItems="flex-start"
                gap={2}
                sx={{
                  py: 1.5, px: 1.5,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                <Box sx={{ width: 4, minHeight: 40, bgcolor: color || 'text.disabled', borderRadius: 1, flexShrink: 0, mt: 0.5 }} />
                {directionIcon(row.fundamentalTrendDirection)}
                <Box flex={1} minWidth={0}>
                  <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                    <ClickableTickerChip ticker={row.ticker} source={source} />
                    <Chip
                      label={`${row.fundamentalTrendLabelPrev || '?'} → ${row.fundamentalTrendLabel || '?'}`}
                      size="small"
                      sx={{ fontSize: '0.65rem', height: 22, bgcolor: `${color || '#666'}14`, color: color || 'text.secondary' }}
                    />
                    <Typography variant="caption" color="text.secondary">
                      Magnitude: {row.fundamentalChangeMagnitude}
                    </Typography>
                  </Box>
                  {row.company && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                      {row.company}
                    </Typography>
                  )}
                  {row.fundamentalText && (
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5, fontStyle: 'italic', whiteSpace: 'pre-line' }}>
                      {row.fundamentalText.length > 300 ? `${row.fundamentalText.slice(0, 300)}…` : row.fundamentalText}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Paper>
      )}

      {/* --- Section 3: Highest & Lowest Risk-Adjusted Quarterly Return --- */}
      <SectionTitle>Risk-Adjusted Quarterly Return — Highest & Lowest</SectionTitle>
      <Box display="flex" gap={2} flexWrap="wrap">
        {[
          { title: 'Highest Risk-Adj Q Return', items: byRiskAdjReturn.top5, color: '#2e7d32', orderBy: s3TopOrderBy, order: s3TopOrder, onSort: handleS3TopSort },
          { title: 'Lowest Risk-Adj Q Return', items: byRiskAdjReturn.bottom5, color: '#c62828', orderBy: s3BotOrderBy, order: s3BotOrder, onSort: handleS3BotSort },
        ].map(({ title, items, color, orderBy, order, onSort }) => (
          <TableContainer key={title} component={Paper} variant="outlined" sx={{ flex: '1 1 340px', minWidth: 320 }}>
            <Typography variant="subtitle2" sx={{ px: 2, pt: 1.5, pb: 0.5, color, fontSize: '0.75rem' }}>
              {title}
            </Typography>
            <MuiTable size="small">
              <TableHead>
                <TableRow>
                  {SECTION3_COLUMNS.map(col => renderHeaderCell(col, orderBy, order, onSort))}
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((row) => (
                  <TableRow key={row.ticker} hover>
                    <TableCell>
                      <ClickableTickerChip ticker={row.ticker} source={source} />
                      {row.company && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.25 }}>
                          {row.company}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color: !isNaN(row.riskAdjQReturn) ? (row.riskAdjQReturn >= 0 ? '#2e7d32' : '#c62828') : undefined }}>
                      {signed(row.riskAdjQReturn)}
                    </TableCell>
                    <TableCell align="right">{signed(row.expectedQReturn)}</TableCell>
                    <TableCell align="right">{num(row.returnProbability, 1)}%</TableCell>
                    <TableCell align="right">{num(row.residualVolatility, 1)}%</TableCell>
                  </TableRow>
                ))}
                {items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography variant="caption" color="text.secondary">No data</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </MuiTable>
          </TableContainer>
        ))}
      </Box>
    </Box>
  );
};

export default PAModal_Fundamentals;
